;(function (angular) {
   'use strict'

   angular.module('azure.util').factory('util', util)

   function util($q, $filter, $window, $sce, $http, config, errorParser) {
      const _sortDirection = Object.freeze({
         ASC: 1,
         DESC: -1,
      })

      function addParamToUrl(param, url) {
         const ampOrQ = url.includes('?') ? '&' : '?'
         return url + ampOrQ + param
      }

      function absoluteUrl(pathAndSearch) {
         return $window.location.protocol + '//' + $window.location.host + pathAndSearch
      }

      /**
       * Normalizes various URL formats to fully qualified URLs.
       * Handles:
       * - Protocol-relative URLs (//example.com)
       * - Relative URLs without protocol (example.com)
       * - Already complete URLs (https://example.com)
       * @param {string} rawUrl - Raw URL string to normalize
       * @returns {string|null} - Normalized URL or null if invalid
       */
      function normalizeRawUrl(rawUrl) {
         if (!rawUrl || typeof rawUrl !== 'string') {
            return null
         }

         const urlString = String(rawUrl || '').trim()
         if (!urlString) {
            return null
         }

         // URL protocol pattern for validation
         const urlProtocolPattern = /^[a-z][a-z0-9+.-]*:/i

         // Already has a protocol scheme
         if (urlProtocolPattern.test(urlString)) {
            return urlString
         }

         // Protocol-relative URL
         if (urlString.startsWith('//')) {
            const protocol = $window.location ? $window.location.protocol : 'https:'
            return protocol + urlString
         }

         // Assume HTTPS for bare domains
         return 'https://' + urlString
      }

      /**
       * Safely parses a URL string without throwing exceptions
       * @param {string} input - URL string to parse
       * @param {string} [base] - Optional base URL for relative URLs
       * @returns {URL|null} - Parsed URL object or null if invalid
       */
      function safeParseUrl(input, base) {
         try {
            return base ? new $window.URL(input, base) : new $window.URL(input)
         } catch {
            return null
         }
      }

      function getSortByPropertyComparer(property, sortDir) {
         if (sortDir === undefined) {
            sortDir = _sortDirection.ASC
         }

         return function (a, b) {
            if (a[property] > b[property]) {
               return sortDir
            }
            if (a[property] < b[property]) {
               return -sortDir
            }
            return 0
         }
      }

      function sortByProperty(property, array) {
         array.sort(getSortByPropertyComparer(property))
         return array
      }

      function sortByPropertyDesc(property, array) {
         array.sort(getSortByPropertyComparer(property, _sortDirection.DESC))
         return array
      }

      function sortByIdDesc(array) {
         return sortByPropertyDesc('id', array)
      }

      function sortByName(array) {
         return sortByProperty('name', array)
      }

      function sumInts(array) {
         if (!Array.isArray(array)) {
            return 0
         }
         return array.reduce(function (sum, item) {
            if (typeof item === 'number') {
               return sum + item
            }
            return sum
         }, 0)
      }

      function capitalize(string) {
         return string.charAt(0).toUpperCase() + string.slice(1)
      }

      function cleanItem(item) {
         if (item) {
            delete item.$promise
            delete item.$resolved
         }
         return item
      }

      function mapToProperty(property, items) {
         return items.map(function (item) {
            return item[property]
         })
      }

      function mapToIds(items) {
         return mapToProperty('id', items)
      }

      function first(items) {
         return items[0]
      }

      // Returns a new array with distinct items.
      function distinct(items) {
         return items.filter(function (item, i) {
            return items.indexOf(item) === i
         })
      }

      // Returns a new array with only truthy items.
      function compact(items) {
         return items.filter(function (item) {
            return item
         })
      }

      function removeById(items, id) {
         for (let i = 0; i < items.length; i++) {
            if (items[i].id === id) {
               items.splice(i, 1)
               break
            }
         }
         return items
      }

      function renameProperty(obj, oldProp, newProp) {
         if (obj && obj[oldProp]) {
            obj[newProp] = obj[oldProp]
            delete obj[oldProp]
         }
      }

      function findByPropertyValue(items, property, propertyValue) {
         return items.find(function (item) {
            return item && item[property] === propertyValue
         })
      }

      function findById(items, id) {
         return findByPropertyValue(items, 'id', id)
      }

      function findIndexByPropertyValue(items, property, propertyValue) {
         return items.findIndex(function (item) {
            return item && item[property] === propertyValue
         })
      }

      function findIndexById(items, id) {
         return findIndexByPropertyValue(items, 'id', id)
      }

      function findOne(haystack, arr) {
         // src: https://stackoverflow.com/a/25926600/1971662
         return arr.some(function (v) {
            return haystack.indexOf(v) >= 0
         })
      }

      function getPagesUntilEnd(getPageFunction, start, limit, resultsArray) {
         resultsArray = resultsArray || []
         return getPageFunction(start, limit).then(function (items) {
            resultsArray.push.apply(resultsArray, items)
            if (items.length < limit) {
               return resultsArray
            }
            return getPagesUntilEnd(getPageFunction, start + limit, limit, resultsArray)
         })
      }

      function getFilteredLength(arraySrc, filterFn) {
         if (!arraySrc || !Array.isArray(arraySrc) || typeof filterFn !== 'function') {
            return 0
         }
         return arraySrc.filter(filterFn).length
      }

      function groupBy(arr, key) {
         function getNestedProperty(obj, keyPath) {
            return keyPath.split('.').reduce((prev, curr) => {
               return prev ? prev[curr] : null
            }, obj)
         }

         const groups = {}
         for (let i = 0; i < arr.length; i++) {
            const entry = arr[i]
            const keyVal = getNestedProperty(entry, key)
            if (!groups[keyVal]) {
               groups[keyVal] = []
            }
            groups[keyVal].push(entry)
         }
         return groups
      }

      function joinArgs() {
         return Array.prototype.slice.call(arguments).join()
      }

      function mapToLowAndHigh(sourceArray) {
         return {
            low: Math.min.apply(null, sourceArray),
            high: Math.max.apply(null, sourceArray),
         }
      }

      function pennyRound(value) {
         return Math.round(value * 100) / 100
      }

      // The first argument to asyncRetryOnce should be an asynchronous function f you
      // want to retry. f should either return a promise or an object with a '$promise'
      // property.  The remaining arguments to asyncRetryOnce will be passed into f.
      // For example:
      //    retryOnce(testAsyncFunction, 1, 2)
      // will call:
      //    testAsyncFunction(1, 2)
      // This will retry on failure, unless the error.status is less than 500. In those
      // cases retrying will probably not do any good.
      function asyncRetryOnce() {
         const thisArg = this
         const f = arguments[0]

         if (typeof f !== 'function') {
            return
         }

         const args = Array.prototype.slice.call(arguments, 1)
         const firstResult = f.apply(thisArg, args)
         if (!firstResult) {
            return
         }

         return (firstResult.$promise || firstResult).catch(function (error) {
            if (0 < error.status && error.status < 500) {
               return $q.reject(error)
            }
            const retryResult = f.apply(thisArg, args)
            return retryResult.$promise || retryResult
         })
      }

      /**
       * Make an HTTP request with retry logic and automatic error parsing for V2 API endpoints
       *
       * This function combines three common patterns for V2 API calls:
       * 1. Using $http for requests
       * 2. Retrying once on 5xx errors
       * 3. Parsing error responses and adding parsedError to the response
       *
       * @param {Object} httpConfig - The $http configuration object (method, url, data, etc.)
       * @returns {Promise} Promise that resolves with the response or rejects with response.parsedError set
       *
       * @example
       * httpRetryOnceWithParsedError({
       *    method: 'GET',
       *    url: config.beehiveApiV2 + '/some-endpoint',
       *    withCredentials: true
       * })
       */
      function httpRetryOnceWithParsedError(httpConfig) {
         return asyncRetryOnce($http, httpConfig).catch(function (response) {
            // Parse the error response and add it to the response object
            response.parsedError = errorParser.parse(response)
            return $q.reject(response)
         })
      }

      function resolveXhr(request) {
         const requestDefer = $q.defer()
         const checkRequestDone = function () {
            if (request.readyState !== XMLHttpRequest.DONE) {
               return false
            }

            if (request.status === 200) {
               requestDefer.resolve(request.responseText)
            } else {
               requestDefer.reject(request)
            }
            return true
         }
         try {
            if (!checkRequestDone()) {
               request.onreadystatechange = checkRequestDone
            }
         } catch (error) {
            console.error(error)
            requestDefer.reject(request)
         }

         return requestDefer.promise
      }

      function getPreviousSundayIfNotSunday(dateSrc) {
         // Note: If the date passed is a Sunday, the passed date is returned.
         const date = new Date(dateSrc)
         return date.setDate(date.getDate() - date.getDay())
      }

      function getFollowingSunday(dateSrc) {
         // Note: The following Sunday is always returned (even it the date passed is a Sunday).
         const date = new Date(dateSrc)
         return date.setDate(date.getDate() + (7 - date.getDay() || 7))
      }

      // Source: https://stackoverflow.com/a/7616484/6352491
      function hashString(input) {
         let hash = 0,
            i,
            chr
         if (input.length === 0) {
            return hash
         }
         for (i = 0; i < input.length; i++) {
            chr = input.charCodeAt(i)
            // Intentionally using bitwise operations for a fast 32-bit hash (see StackOverflow ref above)
            /* eslint-disable-next-line no-bitwise */
            hash = (hash << 5) - hash + chr
            // Truncate to 32-bit signed int (forces wrap-around semantics like other common string hash implementations)
            /* eslint-disable-next-line no-bitwise */
            hash |= 0
         }
         return hash
      }

      function chunk(array, chunkSize) {
         const chunks = []
         for (let i = 0, len = array.length; i < len; i += chunkSize) {
            chunks.push(array.slice(i, i + chunkSize))
         }
         return chunks
      }

      function isArrayOfStrings(input) {
         return (
            Array.isArray(input) &&
            input.every(function (item) {
               return typeof item === 'string'
            })
         )
      }

      function trimStart(str, char) {
         let index = 0
         while (str[index] === char && index < str.length) {
            index++
         }
         return str.substring(index)
      }

      function isStringTrue(str) {
         return str === 'true'
      }

      /**
       * Checks if a value is undefined, null, or an empty/whitespace-only string
       * @param {any} str - Value to check
       * @returns {boolean} - True if the value is undefined, null, or an empty/whitespace-only string
       */
      function isBlankString(str) {
         return str === undefined || str === null || (typeof str === 'string' && str.trim().length === 0)
      }

      function stripHtml(text) {
         return String(text).replace(/<[^>]+>/gm, '')
      }

      /**
       * Safely converts input to HTML string, handling Angular's $sce trusted values
       * @param {any} input - Input value that may be a string, $sce trusted value, or other type
       * @returns {string} - HTML string representation of the input
       */
      function toHtmlString(input) {
         const val = $sce.valueOf(input)
         return typeof val === 'string' ? val : String(val || '')
      }

      /**
       * Creates a temporary DOM container for HTML manipulation.
       * Uses a `div` element to safely parse and manipulate HTML strings.
       * @param {string} html - HTML string to parse
       * @returns {HTMLDivElement} - Container element with parsed HTML
       */
      function createContainer(html) {
         const container = $window.document.createElement('div')
         container.innerHTML = html
         return container
      }

      function isEmptyOrUndefined(arrayOrObject) {
         if (!arrayOrObject) {
            return
         }

         return !Object.keys(arrayOrObject).length
      }

      function pluralize(noun, length) {
         // Note: Currently this only handles +"s" versions of noun pluralization (i.e. not +"ies").
         // If needed for a +"ies" case, update to handle.

         if (!noun) {
            return
         }

         return length && length === 1 ? noun : noun + 's'
      }

      function columnWidthStringRatioToFloat(columnWidth) {
         // Example value for `columnWidth` is `'1/2'`
         const parts = columnWidth.split('/')
         return parseInt(parts[0]) / parseInt(parts[1])
      }

      function normalizeSearchTerm(term) {
         return term
            .toLowerCase()
            .replace(/[-/]/gi, ' ')
            .replace(/[^\w\s]/gi, '')
      }

      function removeSpaces(str) {
         return str.replace(/[\s]/g, '')
      }

      function formatDate(date, format) {
         if (date) {
            return $filter('date')(date, format)
         }
      }

      function formatPhone(number) {
         if (number) {
            return $filter('phone')(number)
         }
      }

      function formatDollars(amount) {
         if (amount) {
            return $filter('dollars')(amount)
         }
      }

      function formatFloat(value, decimalPlaces) {
         // Note that the output here differs from `$filter('number')` in that this does not add
         // decimal places if the value is an integer (see https://docs.angularjs.org/api/ng/filter/number).
         // Also note that this always returns a string.

         decimalPlaces = decimalPlaces || 1
         if (value === null || value === undefined) {
            return '0'
         }
         if (isNaN(value)) {
            return '0'
         }
         if (decimalPlaces === 0) {
            return Math.round(value).toString()
         }
         return parseFloat(value).toFixed(decimalPlaces)
      }

      function hasKey(obj, key) {
         return obj && typeof obj === 'object' && Object.hasOwn(obj, key)
      }

      function isOnline() {
         return $http({
            method: 'HEAD',
            timeout: 5000,
            url: config.api + '/monitor/check',
         })
            .then(function () {
               return true
            })
            .catch(function () {
               return $q.reject(false)
            })
      }

      //================================================================================
      // Date/Time Related
      //================================================================================

      function areDatesSameMonth(date1Src, date2Src) {
         const date1 = new Date(date1Src)
         const date2 = new Date(date2Src)
         return getMonthFromDate(date1) === getMonthFromDate(date2)
      }

      function formatTotalMinutes(totalMinutes) {
         const hours = Math.floor(totalMinutes / 60)
         const minutes = totalMinutes % 60

         function getMinutesDisplay() {
            if (minutes === 1) {
               return minutes + ' minute'
            } else if (minutes > 0) {
               return minutes + ' minutes'
            } else {
               return ''
            }
         }

         if (hours === 1) {
            return '1 hour ' + getMinutesDisplay()
         } else if (hours > 1) {
            return hours + ' hours ' + getMinutesDisplay()
         } else {
            return getMinutesDisplay()
         }
      }

      function getDateRangeForYear(year) {
         return [year + '-01-01', year + '-12-31']
      }

      function getMonthFromDate(dateObj) {
         return dateObj.toLocaleString('en-US', {
            month: 'long',
         })
      }

      function isDateNotCurrentYear(dateVal) {
         if (!dateVal) {
            return
         }
         const currentYear = new Date().getFullYear()
         const yearOfDateInQuestion = parseInt($filter('date')(dateVal, 'yyyy')) // Note: Angular's $filter returns a string
         return yearOfDateInQuestion !== currentYear
      }

      function isDateBeforeToday(dateObj) {
         if (!dateObj) {
            return
         }
         const today = new Date()
         today.setHours(0, 0, 0, 0) // Set the time of 'today' to midnight to only compare the date part

         return dateObj < today
      }

      //================================================================================

      return {
         addParamToUrl: addParamToUrl,
         absoluteUrl: absoluteUrl,
         normalizeRawUrl: normalizeRawUrl,
         safeParseUrl: safeParseUrl,
         cleanItem: cleanItem,
         mapToProperty: mapToProperty,
         mapToIds: mapToIds,
         first: first,
         distinct: distinct,
         capitalize: capitalize,
         compact: compact,
         sortDirection: _sortDirection,
         getSortByPropertyComparer: getSortByPropertyComparer,
         sortByProperty: sortByProperty,
         sortByPropertyDesc: sortByPropertyDesc,
         sortByIdDesc: sortByIdDesc,
         sortByName: sortByName,
         sumInts: sumInts,
         removeById: removeById,
         renameProperty: renameProperty,
         findByPropertyValue: findByPropertyValue,
         findIndexByPropertyValue: findIndexByPropertyValue,
         findById: findById,
         findIndexById: findIndexById,
         findOne: findOne,
         getPagesUntilEnd: getPagesUntilEnd,
         getFilteredLength: getFilteredLength,
         groupBy: groupBy,
         joinArgs: joinArgs,
         mapToLowAndHigh: mapToLowAndHigh,
         pennyRound: pennyRound,
         asyncRetryOnce: asyncRetryOnce,
         httpRetryOnceWithParsedError: httpRetryOnceWithParsedError,
         resolveXhr: resolveXhr,
         getPreviousSundayIfNotSunday: getPreviousSundayIfNotSunday,
         getFollowingSunday: getFollowingSunday,
         hashString: hashString,
         chunk: chunk,
         isArrayOfStrings: isArrayOfStrings,
         trimStart: trimStart,
         isStringTrue: isStringTrue,
         isBlankString: isBlankString,
         stripHtml: stripHtml,
         toHtmlString: toHtmlString,
         createContainer: createContainer,
         isEmptyOrUndefined: isEmptyOrUndefined,
         pluralize: pluralize,
         columnWidthStringRatioToFloat: columnWidthStringRatioToFloat,
         normalizeSearchTerm: normalizeSearchTerm,
         removeSpaces: removeSpaces,
         formatDate: formatDate,
         formatPhone: formatPhone,
         formatDollars: formatDollars,
         formatFloat: formatFloat,
         hasKey: hasKey,
         isOnline: isOnline,
         // Date/time related
         areDatesSameMonth: areDatesSameMonth,
         formatTotalMinutes: formatTotalMinutes,
         getDateRangeForYear: getDateRangeForYear,
         getMonthFromDate: getMonthFromDate,
         isDateNotCurrentYear: isDateNotCurrentYear,
         isDateBeforeToday: isDateBeforeToday,
      }
   }
})(angular)
